import classNames from 'classnames';
import { BILLING_FREQUENCIES, Frequency, Tier } from './subscriptions.constants';
import { FeatureCategory } from './FeatureCategory';
import { Button } from '../_misc/buttons/Button';
import { useTheme } from '../../context/theme/useTheme';
import { ButtonType } from '../_misc/buttons/ButtonType';
import { useAuth } from '../../context/auth/useAuth';
import { useNavigate } from 'react-router-dom';
import { usePurchase } from '../../context/purchase/usePurchase';
import { ThemeBackground, ThemePrimary, ThemeText } from '../../context/theme/ThemeConstantsUpdated';
import CheckCircleIcon from '@heroicons/react/20/solid/CheckCircleIcon';

const { mainBackground, borderBackground } = ThemeBackground;
const { primaryBackground, primaryBorderIntense, primaryBorderMuted } = ThemePrimary;
const { mainText, secondaryText } = ThemeText;

export const TierCard = (props: Props) => {
  const { tier, proceed } = props;
  const { currentUser } = useAuth();
  const { toggleSubscribing } = usePurchase();
  const { name, id, price, features, tierTitle } = tier;
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const frequency = BILLING_FREQUENCIES[0];
  const otherFrequency = BILLING_FREQUENCIES[1];
  const otherPrice = tier.price[(otherFrequency.value as 'monthly') || 'semianually'];
  const isActive = currentUser?.badges.includes(tier.badge);

  const onClick = () => {
    if (isActive) {
      navigate(`/user/${currentUser!.id}?tab=subscription`);
      toggleSubscribing();
    } else {
      proceed(tier, frequency);
    }
  };

  return (
    <div
      key={id}
      className={classNames(
        isActive ? primaryBackground : mainBackground,
        'flex h-full flex-col rounded-3xl px-8 pb-5 pt-8'
      )}
    >
      <div className="mb-3 flex items-center justify-between gap-x-4">
        <h3 id={id} className={classNames(isActive ? 'text-white' : mainText, 'text-2xl font-semibold leading-8')}>
          {name}
        </h3>
        {isActive && <CheckCircleIcon className="h-8 w-8 text-white" />}
      </div>

      <div
        className={classNames(
          isActive ? (isDarkMode ? primaryBorderIntense : primaryBorderMuted) : borderBackground,
          isActive && 'mb-5',
          'border-y-2',
          'flex grow flex-col border-y-2 px-1 py-5'
        )}
      >
        <FeatureCategory label="PERKS" features={features.map((f) => f.label)} isActive={isActive} />
        <div className="h-3" />
      </div>

      {!isActive && (
        <p className="mb-4 mt-5 flex items-baseline gap-x-1">
          <span className={classNames(mainText, 'text-4xl font-bold tracking-tight')}>
            {(price as { monthly: string; semiAnnually: string })[(frequency!.value as 'monthly') || 'semiAnnually']}
          </span>
          <span className={classNames(secondaryText, 'text-sm font-semibold leading-6')}>{frequency!.priceSuffix}</span>
        </p>
      )}

      <Button
        onClick={() => onClick()}
        options={{ isFullWidth: true }}
        buttonType={isActive ? ButtonType.Light : undefined}
        className={classNames(isActive && 'mb-2')}
      >
        <span className="text-lg">{isActive ? 'Manage Subscription' : `Become a ${tierTitle}`}</span>
      </Button>

      {!isActive && (
        <div className="flex items-center justify-center pt-2">
          <Button onClick={() => proceed(tier, otherFrequency!)} buttonType={ButtonType.PrimaryLink}>
            Or pay {otherPrice} {otherFrequency.priceSuffix}
          </Button>
        </div>
      )}
    </div>
  );
};

type Props = {
  tier: Tier;
  proceed: (tier: Tier, freq: Frequency) => void;
};
